import ConfirmationModal from '@components/ConfirmationModal'
import { useUpdateTrip } from '@hooks/useUpdateTrip'
import { getDeviceContext } from '@lib/analytics'
import { joinApprovalExplanation } from '@lib/joinRequests'
import { membershipFailure, stopApprovingConfirmation } from '@lib/membership'
import React, { useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { SectionDivider } from './EditTripModal.styles'
import SwitchRow from './SwitchRow'

interface JoinApprovalSectionProps {
	tripId: string
	/** Named in the confirm before the switch goes off. */
	tripName: string
	/** Only the owner changes it; everyone else sees it read-only. */
	isOwner: boolean
	joinApprovalRequired: boolean
}

/**
 * The "Require Join Approval" switch (Join approval). It flips at once and
 * auto-saves, apart from the form's Save and Cancel, and puts itself back if the
 * save fails. Turning it off lets everyone waiting in — the server approves them —
 * so it asks first.
 */
const JoinApprovalSection: React.FC<JoinApprovalSectionProps> = ({
	tripId,
	tripName,
	isOwner,
	joinApprovalRequired,
}) => {
	const updateTrip = useUpdateTrip(tripId)
	const [checked, setChecked] = useState(joinApprovalRequired)
	const [isConfirmingOff, setIsConfirmingOff] = useState(false)

	useEffect(() => setChecked(joinApprovalRequired), [joinApprovalRequired])

	const save = (next: boolean) => {
		setChecked(next)
		updateTrip.mutate(
			{ joinApprovalRequired: next, analytics: getDeviceContext() },
			{
				onError: error => {
					setChecked(!next)
					const message = membershipFailure({ kind: 'updateApproval' }, error)
					if (message) toast.error(message)
				},
			},
		)
	}

	const toggle = () => {
		if (checked) setIsConfirmingOff(true)
		else save(true)
	}

	const disabled = !isOwner || updateTrip.isPending
	const confirm = stopApprovingConfirmation(tripName)

	return (
		<>
			<SectionDivider />
			<SwitchRow
				id="join-approval"
				title="Require Join Approval"
				description={joinApprovalExplanation({ isRequired: checked, isEditable: isOwner })}
				checked={checked}
				disabled={disabled}
				onChange={toggle}
			/>

			<ConfirmationModal
				isOpen={isConfirmingOff}
				onClose={() => setIsConfirmingOff(false)}
				onConfirm={() => save(false)}
				title={confirm.title}
				message={confirm.message}
				confirmText={confirm.confirmText}
				cancelText={confirm.cancelText}
				variant="danger"
			/>
		</>
	)
}

export default JoinApprovalSection
